import { useMutation } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";

import { Alert } from "../../../components/ui/Alert";
import { Button } from "../../../components/ui/Button";
import { FormField } from "../../../components/ui/FormField";
import { Input } from "../../../components/ui/Input";
import { ApiError } from "../../../lib/api";
import {
  type BootstrapTokenValidityMode,
  type IssuedBootstrapToken,
  bootstrapTokensApi,
} from "../../../lib/api/bootstrap";
import { t } from "../../../lib/i18n";

interface CreateBootstrapTokenFormProps {
  onCreated: (issued: IssuedBootstrapToken) => void;
  onCancel: () => void;
}

const VALIDITY_MODES: BootstrapTokenValidityMode[] = [
  "permanent",
  "rotating",
  "limited_use",
  "short_lived",
];

export function CreateBootstrapTokenForm({
  onCreated,
  onCancel,
}: CreateBootstrapTokenFormProps) {
  const [label, setLabel] = useState("");
  const [validityMode, setValidityMode] =
    useState<BootstrapTokenValidityMode>("permanent");
  const [maxUses, setMaxUses] = useState<string>("");

  const mutate = useMutation({
    mutationFn: () =>
      bootstrapTokensApi.create({
        label,
        validity_mode: validityMode,
        max_uses:
          validityMode === "limited_use" && maxUses ? Number(maxUses) : null,
      }),
    onSuccess: onCreated,
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!label.trim()) return;
    mutate.mutate();
  };

  const errMsg =
    mutate.error instanceof ApiError
      ? t(`errors.${mutate.error.code}`, mutate.error.message)
      : mutate.error
        ? t("errors.network")
        : null;

  return (
    <form
      onSubmit={onSubmit}
      style={{ display: "flex", flexDirection: "column", gap: 16 }}
    >
      {errMsg && <Alert tone="danger">{errMsg}</Alert>}

      <FormField label={t("admin.bootstrap.label.field")} required>
        {(id) => (
          <Input
            id={id}
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={t("admin.bootstrap.label.placeholder")}
            required
          />
        )}
      </FormField>

      <FormField label={t("admin.bootstrap.validityMode.field")} required>
        {(id) => (
          <select
            id={id}
            className="input"
            value={validityMode}
            onChange={(e) =>
              setValidityMode(e.target.value as BootstrapTokenValidityMode)
            }
          >
            {VALIDITY_MODES.map((mode) => (
              <option key={mode} value={mode}>
                {t(`admin.bootstrap.validityMode.${mode}`)}
              </option>
            ))}
          </select>
        )}
      </FormField>

      {validityMode === "limited_use" && (
        <FormField
          label={t("admin.bootstrap.maxUses.field")}
          hint={t("admin.bootstrap.maxUses.hint")}
          required
        >
          {(id) => (
            <Input
              id={id}
              type="number"
              min={1}
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              required
            />
          )}
        </FormField>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Button variant="ghost" type="button" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
        <Button type="submit" loading={mutate.isPending}>
          {t("admin.bootstrap.generate")}
        </Button>
      </div>
    </form>
  );
}
